// Joana render layer — the render-target abstraction (joana-render-spec §5). The
// ONLY surface a render target implements, so the patch protocol stays
// target-agnostic. The DOM renderer (dom-renderer.ts) is the first impl;
// Canvas/WebGL/native are future impls behind this same interface — a swap, not
// a rewrite. RecordingRenderer (below) is the in-memory impl used by tests.

import type { Op, PatchFrame, RenderEvent } from "./patch.ts";

export interface Renderer {
  createNode(id: string, type: string): void;
  insert(id: string, parent: string | null, before: string | null): void;
  remove(id: string): void;
  setProp(id: string, key: string, value: unknown): void;
  setText(id: string, value: string): void;
  setStyle(id: string, patch: Record<string, unknown>): void;
  bindEvent(id: string, event: string, h: string): void;
  unbindEvent(id: string, event: string): void;
  /** Commit a batch (one paint). DOM applies eagerly; this is the frame fence. */
  flush(frame: number): void;
}

/** Apply one patch frame to a renderer, in order, then flush. */
export function applyFrame(renderer: Renderer, frame: PatchFrame): void {
  for (const op of frame.ops) applyOp(renderer, op);
  renderer.flush(frame.frame);
}

function applyOp(r: Renderer, op: Op): void {
  switch (op.op) {
    case "create": return r.createNode(op.id, op.type);
    case "insert": return r.insert(op.id, op.parent, op.before);
    case "remove": return r.remove(op.id);
    case "prop": return r.setProp(op.id, op.key, op.value);
    case "text": return r.setText(op.id, op.value);
    case "style": return r.setStyle(op.id, op.patch);
    case "bind": return r.bindEvent(op.id, op.event, op.h);
    case "unbind": return r.unbindEvent(op.id, op.event);
  }
}

// --- RecordingRenderer -----------------------------------------------------

interface RNode {
  id: string;
  type: string;
  props: Map<string, unknown>;
  style: Map<string, unknown>;
  text: string | null;
  events: Map<string, string>;
  children: string[];
}

/**
 * An in-memory Renderer that records the resulting forest. Used by tests to
 * assert the patch-protocol invariant — that applying an incremental diff
 * converges to the same tree as a from-scratch render. Also a reference for what
 * any concrete renderer must end up representing.
 */
export class RecordingRenderer implements Renderer {
  private readonly nodes = new Map<string, RNode>();
  private readonly roots: string[] = [];

  createNode(id: string, type: string): void {
    this.nodes.set(id, {
      id,
      type,
      props: new Map(),
      style: new Map(),
      text: null,
      events: new Map(),
      children: [],
    });
  }

  insert(id: string, parent: string | null, before: string | null): void {
    this.detach(id);
    const siblings = parent === null ? this.roots : this.nodes.get(parent)!.children;
    const idx = before === null ? -1 : siblings.indexOf(before);
    if (idx < 0) siblings.push(id);
    else siblings.splice(idx, 0, id);
  }

  remove(id: string): void {
    this.detach(id);
    this.destroy(id);
  }

  setProp(id: string, key: string, value: unknown): void {
    const n = this.nodes.get(id);
    if (!n) return;
    if (value === null) n.props.delete(key);
    else n.props.set(key, value);
  }

  setText(id: string, value: string): void {
    const n = this.nodes.get(id);
    if (n) n.text = value;
  }

  setStyle(id: string, patch: Record<string, unknown>): void {
    const n = this.nodes.get(id);
    if (!n) return;
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) n.style.delete(k);
      else n.style.set(k, v);
    }
  }

  bindEvent(id: string, event: string, h: string): void {
    this.nodes.get(id)?.events.set(event, h);
  }

  unbindEvent(id: string, event: string): void {
    this.nodes.get(id)?.events.delete(event);
  }

  flush(): void {}

  /** Capture an event as if a user fired it (drives the round-trip in tests). */
  fire(id: string, event: string, payload?: Record<string, unknown>): RenderEvent | null {
    const h = this.nodes.get(id)?.events.get(event);
    return h === undefined ? null : { id, event, h, payload };
  }

  /** Canonical string of the rendered forest, for equality assertions. */
  serialize(): string {
    return JSON.stringify(this.roots.map((id) => this.snapshot(id)));
  }

  /**
   * The same, for ONE subtree.
   *
   * Needed once the shell paints more than one thing at a time: the three-pane
   * frame draws the chat list BESIDE the open conversation, and the list's
   * preview row carries the newest message's text — so a forest-wide count of
   * a message body reads it twice and calls the second one a duplicate bubble.
   * An assertion about the thread has to be able to say "in the thread".
   *
   * Empty string when the id is not mounted, which reads as "no matches" to a
   * count and as a failure to an `includes` — both of which are the answer a
   * missing subtree deserves.
   */
  serializeFrom(id: string): string {
    return this.nodes.has(id) ? JSON.stringify(this.snapshot(id)) : "";
  }

  private snapshot(id: string): unknown {
    const n = this.nodes.get(id)!;
    return {
      type: n.type,
      props: sortedObject(n.props),
      style: sortedObject(n.style),
      text: n.text,
      events: sortedObject(n.events),
      children: n.children.map((c) => this.snapshot(c)),
    };
  }

  private detach(id: string): void {
    const ri = this.roots.indexOf(id);
    if (ri >= 0) {
      this.roots.splice(ri, 1);
      return;
    }
    for (const n of this.nodes.values()) {
      const ci = n.children.indexOf(id);
      if (ci >= 0) {
        n.children.splice(ci, 1);
        return;
      }
    }
  }

  private destroy(id: string): void {
    const n = this.nodes.get(id);
    if (!n) return;
    for (const c of [...n.children]) this.destroy(c);
    this.nodes.delete(id);
  }
}

function sortedObject(m: Map<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of [...m.keys()].sort()) out[k] = m.get(k);
  return out;
}
